import { extend } from "@dhx/ts-common/core";
import { EventSystem } from "@dhx/ts-common/events";
import { IView, toViewLike, View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents } from "@dhx/ts-data";
import { ILayout, Layout } from "@dhx/ts-layout";
import { Toolbar } from "@dhx/ts-toolbar";
import { NavigationBarEvents } from "@dhx/ts-navbar";
import locale from "./locales/en";
import {
	FileStatus,
	IFileWrapper,
	IUploader,
	IVault,
	IVaultConfig,
	IVaultEventSystem,
	IVaultProgressData,
	ProgressBarEvents,
	UploaderEvents,
	VaultMode,
} from "./types";
import { Uploader } from "./Uploader";
import { gridTemplate, listTemplate, getContainersEvents } from "./configs";
import { isImage, removeItem } from "./helper";
import { ProgressBar } from "./ProgressBar";
import { ReadStackPreview } from "./ReadStackPreview";
import { DataView } from "@dhx/ts-dataview";
import { List } from "@dhx/ts-list";
import { create, el } from "@dhx/ts-common/dom";
import { isFirefox } from "@dhx/ts-common/html";

export class Vault extends View implements IVault {
	public config: IVaultConfig;
	public data: DataCollection<IFileWrapper>;
	public events: IVaultEventSystem;
	public uploader: IUploader;
	public toolbar: Toolbar;
	public list: List;
	public grid: DataView;

	private _layout: ILayout;
	private _emptyField: IView;
	private _activeView: IView | DataView | List;
	private _readStack: ReadStackPreview;
	private _canDrop: boolean;
	private readonly _progressBar: ProgressBar<IVaultProgressData>;

	constructor(container: HTMLElement | string, config: IVaultConfig = {}) {
		super(
			null,
			extend(
				{
					mode: VaultMode.list,
					modeControls: true,
					toolbar: true,
					updateFromResponse: true,
					scaleFactor: 4,
					customScroll: true, // TODO: doing nothing now
					uploader: {},
					progressBar: {},
					disablePreview: false,
					editable: true,
				},
				config
			)
		);

		if (!this.config.toolbar) {
			this.config.uploader.autosend = true;
		}

		if (config.data) {
			this.data = config.data;
			this.events = config.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem(this);
			this.data = new DataCollection<IFileWrapper>({}, this.events);
		}

		this.data.config.init = obj => {
			obj.status = obj.status || FileStatus.uploaded;
			if (obj.file) {
				obj.size = obj.file.size;
				obj.name = obj.file.name;
				obj.value = obj.file.name
					?.split(".")
					.slice(0, -1)
					.join(".");
				obj.extension = obj.file.name?.split(".").pop();
				obj.preview = !this.config.disablePreview;
			} else {
				obj.size = obj.size || 0;
				obj.name = obj.name || "";
				obj.value =
					obj.name
						?.split(".")
						.slice(0, -1)
						.join(".") || "";
				obj.extension = obj.name?.split(".").pop();
				obj.preview = !this.config.disablePreview;
			}
			if (obj.file && isImage(obj)) {
				this._readStack.add(obj, this.uploader.config.autosend || this.config.disablePreview);
			}
			return obj;
		};

		this._readStack = new ReadStackPreview(this.data);

		this.uploader = new Uploader(this.config.uploader, this.data, this.events);

		this._progressBar = new ProgressBar(this.events, this.config.progressBar);
		this.events.on(UploaderEvents.uploadProgress, (progress, current, total) =>
			this._progressBar.setState(progress, { current, total })
		);

		this._initUI(container);

		this._initEvents();
	}

	destructor() {
		this.toolbar.destructor();
		this._readStack.stop();
		this.uploader.unlinkDropArea();
		this.uploader.abort();
		this.unmount();
	}

	getRootView() {
		return this._layout.getRootView();
	}

	paint() {
		const isEmpty = this._canDrop || !this.data.getLength();
		this._activeView = isEmpty
			? this._emptyField
			: this.config.mode === VaultMode.grid ||
			  (this.toolbar && this.toolbar.getState().mode === VaultMode.grid)
			? this.grid
			: this.list;
		this._layout.getCell("vault").attach(this._activeView);
	}

	private _initUI(container) {
		const vaultElements = [
			{
				id: "toolbar",
				css: "dhx_vault--toolbar",
				height: "content",
			},
			{
				id: "vault",
				css: "dhx_vault--container",
			},
		];

		const layout = (this._layout = new Layout(container, {
			css: `dhx_vault ${this.config.customScroll ? "dhx_vault__custom-scroll" : ""}`,
			rows: vaultElements,
			on: this._getDragEvents(),
		}));

		this._initToolbar();
		layout.getCell("toolbar").attach(this.toolbar);

		this._initEmpty();
		this._initList();
		this._initGrid(container);

		if (!this.config.toolbar) {
			layout.getCell("toolbar").hide();
		}

		this.paint();
	}

	private _initList() {
		const supportsTouch = "ontouchstart" in document.documentElement;
		this.list = new List(null, {
			data: this.data,
			dragMode: "both",
			multiselection: supportsTouch ? true : "ctrlClick",
			template: listTemplate,
			eventHandlers: getContainersEvents(this.data),
			keyNavigation: true,
			editable: this.config.editable,
			itemHeight: 49,
			hotkeys: {
				delete: () => {
					const selected = this.list.selection.getItem()?.map(item => item.id);
					if (selected.length) this.data.remove(selected);
				},
			},
		});
		this.list.events.on("afterEditEnd", (value, id) => {
			const item = this.list.data.getItem(id);
			this.list.data.update(id, { name: `${value}.${item.extension}` });
		});
	}

	private _initGrid(container) {
		const supportsTouch = "ontouchstart" in document.documentElement;
		this.grid = new DataView(null, {
			gap: 8,
			itemsInRow: 4,
			dragMode: "both",
			multiselection: supportsTouch ? true : "ctrlClick",
			data: this.data,
			template: gridTemplate,
			eventHandlers: getContainersEvents(this.data),
			keyNavigation: true,
			editable: this.config.editable,
			hotkeys: {
				delete: () => {
					const selected = this.list.selection.getItem()?.map(item => item.id);
					if (selected.length) this.data.remove(selected);
				},
			},
		});
		this.grid.selection = this.list.selection;
		if (container) {
			if (typeof container === "string") {
				container = document.getElementById(container);
			}
			this.grid.config.itemsInRow = Math.floor(container.offsetWidth / 100);
		}
	}

	private _initToolbar() {
		this.toolbar = new Toolbar(null, { css: "vault-toolbar" });
		this.toolbar.data.parse([
			{
				id: "add",
				tooltip: locale.add,
				type: "navItem",
				css: "dhx_toolbar-button--circle dhx_toolbar-button--icon",
				icon: "dxi dxi-plus",
			},
			{
				id: "mode-separator",
				type: "separator",
			},
			{
				id: "list",
				tooltip: locale.list,
				type: "navItem",
				css: "dhx_toolbar-button--circle dhx_toolbar-button--icon",
				icon: "dxi dxi-view-sequential",
				active: this.config.mode === VaultMode.list,
				group: "mode",
			},
			{
				id: "grid",
				tooltip: locale.grid,
				type: "navItem",
				css: "dhx_toolbar-button--circle dhx_toolbar-button--icon",
				icon: "dxi dxi-view-grid",
				active: this.config.mode === VaultMode.grid,
				group: "mode",
			},
			{
				id: "upload-separator",
				type: "separator",
			},
			{
				id: "upload",
				tooltip: locale.upload,
				type: "navItem",
				css: "dhx_toolbar-button--circle dhx_toolbar-button--icon",
				icon: "dxi dxi-vault",
			},
			{
				id: "spacer",
				type: "spacer",
			},
			{
				id: "remove-all",
				tooltip: locale.clearAll,
				type: "navItem",
				css: "dhx_toolbar-button--circle dhx_toolbar-button--icon",
				icon: "dxi dxi-delete-forever",
			},
		]);

		if (!this.config.modeControls) {
			this.toolbar.hide(["mode-separator", "list", "grid", "upload-separator"]);
		}

		this._hideAdditionalButtons();
	}

	private _initEmpty() {
		return (this._emptyField = toViewLike(
			create({
				render: () =>
					el(".dhx-dropable-area.drop-files-here", [
						el(".dhx-big-icon-block", [el(".dxi.dxi-vault")]),
						!this._canDrop && el(".drop-area-bold-text", locale.dragAndDrop),
						!this._canDrop && el(".drop-area-bold-text", locale.filesOrFoldersHere),
						!this._canDrop && el(".drop-area-light-text", locale.or),
						!this._canDrop &&
							el(
								"button.dhx_button.dhx_button--view_flat.dhx_btn--small.dhx_button--color_primary.dhx_button--size_small",
								{
									type: "button",
									onclick: () => this.uploader.selectFile(),
								},
								locale.browse
							),
					]),
			})
		));
	}

	private _changeUI(mode) {
		this.config.mode = mode;
		this._activeView = mode === VaultMode.grid ? this.grid : this.list;
		if (!this.data.getLength()) {
			return;
		}
		this.paint();
	}

	private _initEvents() {
		this.data.events.on(DataEvents.change, (id, status) => {
			if (!this.data.getLength()) {
				this._hideAdditionalButtons();
			} else {
				this._showAdditionalButtons();
			}
			if (this.config.downloadURL) {
				this.data.forEach(item => {
					if (!item.downloadURL) {
						item.downloadURL = this.config.downloadURL;
					}
				});
			}
			this.paint();
		});

		this.events.on(UploaderEvents.uploadBegin, () => {
			if (this.config.toolbar) {
				this._layout.getCell("toolbar").attach(this._progressBar);
			}
		});

		this.events.on(UploaderEvents.uploadComplete, () => {
			if (this.config.toolbar) {
				this._layout.getCell("toolbar").attach(this.toolbar);
			}
		});

		this.toolbar.events.on(NavigationBarEvents.click, id => {
			switch (id) {
				case "add":
					this.uploader.selectFile();
					break;
				case "remove-all":
					if (this.data.getLength()) {
						const items = (this._activeView as any).selection.getItem();
						if (items && items.length > 0) {
							items.forEach(item => {
								removeItem(this.data, item.id);
							});
						} else {
							this.data.removeAll();
						}
					}
					break;
				case "list":
				case "grid":
					this._changeUI(id);
					break;
				case "upload":
					this.uploader.send();
					break;
			}
		});

		this.events.on(ProgressBarEvents.cancel, () => {
			this.uploader.abort();
			this.paint();
		});
	}

	private _getDragEvents() {
		const rect = {
			left: null,
			top: null,
			width: null,
			height: null,
		};

		return {
			dragleave: (e: DragEvent) => {
				if (!this._canDrop) {
					return;
				}
				if (
					e.pageX > rect.left + rect.width - 1 ||
					e.pageX < rect.left ||
					e.pageY > rect.top + rect.height - 1 ||
					e.pageY < rect.top
				) {
					this._canDrop = false;
					if (this.config.toolbar) {
						this._layout.getCell("toolbar").show();
					}
					this._layout.config.css = "dhx_vault";
					this.paint();
				}
			},
			dragenter: (e: DragEvent) => {
				e.preventDefault();

				if (this.uploader.isActive || this._canDrop) {
					return;
				}

				const types = e.dataTransfer.types;
				for (const type of types) {
					if (
						(type !== "Files" && type !== "application/x-moz-file" && type !== "text/plain") ||
						(type === "text/plain" && isFirefox())
					) {
						this._canDrop = false;
						return;
					}
				}

				this._canDrop = true;

				const clientRect = this.getRootView().node.el.getBoundingClientRect();

				rect.left = clientRect.left + window.pageXOffset;
				rect.top = clientRect.top + window.pageYOffset;
				rect.width = clientRect.width;
				rect.height = clientRect.height;

				this._canDrop = true;
				if (this.config.toolbar) {
					this._layout.getCell("toolbar").hide();
				}

				this._layout.config.css = `dhx_vault dhx-dragin ${this._canDrop ? " drop-here" : ""}`;
				this.paint();
			},
			dragover: (e: DragEvent) => {
				e.preventDefault();
			},
			drop: (e: DragEvent) => {
				e.preventDefault();

				if (!this._canDrop) {
					return;
				}

				const dataTransfer = e.dataTransfer;
				this.uploader.parseFiles(dataTransfer);
				this._canDrop = false;
				if (this.config.toolbar) {
					this._layout.getCell("toolbar").show();
				}
				this._layout.config.css = "dhx_vault";
				this.paint();
			},
		};
	}

	private _hideAdditionalButtons() {
		const modeControls = ["mode-separator", "list", "grid"];
		const uploadControls = ["upload", this.config.modeControls ? "upload-separator" : ""];
		const elementsList = [...modeControls, ...uploadControls, "remove-all"];

		elementsList.forEach(item => {
			if (this.toolbar.data.getItem(item) && !this.toolbar.data.getItem(item).hidden) {
				this.toolbar.hide(item);
			}
		});
	}

	private _showAdditionalButtons() {
		const modeControls = this.config.modeControls ? ["mode-separator", "list", "grid"] : [];
		const uploadControls = !this.uploader.config.autosend
			? ["upload", this.config.modeControls ? "upload-separator" : ""]
			: [];
		const elementsList = [...modeControls, ...uploadControls, "remove-all"];

		this.toolbar.data.update("remove-all", {
			tooltip:
				(this._activeView as any).selection && (this._activeView as any).selection.getItem()
					? locale.clearAllSelected
					: locale.clearAll,
		});

		elementsList.forEach(item => {
			if (this.toolbar.data.getItem(item) && this.toolbar.data.getItem(item).hidden) {
				this.toolbar.show(item);
			}
		});
	}
}
